import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Checkbox, Menu, MenuItem } from '@material-ui/core';
import styled from 'styled-components';
import { AppointmentData } from '../../models';
import { addPaymentToClientAppointmentsAsync, sendClientAppointmentsCheckoutEmailAsync } from '../../ajax/requests';
import { alertError, alertSuccess } from '../../store/alerts/actions';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getAppointmentTotalPayments, getTotalPrice, serviceName } from '../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../store/settings/hooks';
import { formatEuros } from '../../shared/string';
import { toDateOnlyString, Guid } from '../../shared/utils';
import Actions from '../../store/actions';
import AppButton from '../../components/AppButton';
import { PaymentLabel } from 'src/components/Forms/fields/PaymentsList';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const SelectAllRow = styled.div<{ children?: React.ReactNode; onClick?: () => void }>`
    display: flex;
    align-items: center;
    padding: var(--spacer-XS) var(--spacer-S);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    margin-bottom: var(--spacer-XS);
    font-weight: bold;
    color: var(--text-body);
    cursor: pointer;
    user-select: none;
`;

const AppointmentRow = styled.div<{ $selected: boolean; children?: React.ReactNode; onClick?: () => void }>`
    display: flex;
    align-items: center;
    padding: var(--spacer-XS) var(--spacer-S);
    border-radius: 4px;
    cursor: pointer;
    background-color: ${p => p.$selected ? 'var(--bg-hover-light)' : 'transparent'};
    transition: background-color 0.15s;

    &:hover {
        background-color: var(--bg-hover-light);
    }
`;

const AppointmentInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const AppointmentDate = styled.span`
    font-weight: 600;
    color: var(--text-body);
    font-size: 0.9rem;
`;

const AppointmentType = styled.span`
    color: var(--text-muted);
    font-size: 0.85rem;
    margin-left: var(--spacer-XS);
`;

const MissingAmount = styled.span`
    font-weight: 600;
    color: var(--danger-color);
    font-size: 0.9rem;
    white-space: nowrap;
    margin-left: var(--spacer-S);
`;

const TotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacer-XS) var(--spacer-S);
    border-top: var(--border-thin) solid var(--appLigthGrey);
    margin-top: var(--spacer-XS);
    font-size: 0.9rem;
    color: var(--text-muted);
`;

const TotalAmount = styled.span`
    font-weight: bold;
    color: var(--text-body);
`;

const EmptyState = styled.p`
    color: var(--text-muted);
    text-align: center;
    padding: var(--spacer-M) 0;
    margin: 0;
`;

const FooterActions = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ClientSendPaymentLinkProps {
    clientId: string;
    appointments: AppointmentData[];
}

const PAGE_SIZE = 20;

const ClientSendPaymentLink: React.FC<ClientSendPaymentLinkProps> = ({ clientId, appointments }) => {
    const dispatch = useDispatch();
    const t = useTranslations();
    const generalSettings = useGeneralSettings();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [paymentMenuAnchor, setPaymentMenuAnchor] = useState<HTMLElement | null>(null);

    const unpaidAppointments = useMemo(() =>
        appointments
            .filter(a => !a.packId && getAppointmentTotalPayments(a) < getTotalPrice(a))
            .sort((a, b) => (a.date ?? '') < (b.date ?? '') ? -1 : 1),
        [appointments]);

    const visibleAppointments = useMemo(() =>
        unpaidAppointments.slice(0, visibleCount),
        [unpaidAppointments, visibleCount]);

    const allSelected = selectedIds.length === unpaidAppointments.length && unpaidAppointments.length > 0;
    const someSelected = selectedIds.length > 0 && !allSelected;

    const selectedTotal = useMemo(() =>
        unpaidAppointments
            .filter(a => selectedIds.includes(a.id))
            .reduce((sum, a) => sum + (getTotalPrice(a) * (1 + a.vat) - getAppointmentTotalPayments(a)), 0),
        [unpaidAppointments, selectedIds]);

    const open = useCallback(() => {
        setVisibleCount(PAGE_SIZE);
        setSelectedIds(unpaidAppointments.map(a => a.id));
        setModalOpen(true);
    }, [unpaidAppointments]);

    const toggle = useCallback((id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }, []);

    const toggleAll = useCallback(() => {
        setSelectedIds(allSelected ? [] : unpaidAppointments.map(a => a.id));
    }, [allSelected, unpaidAppointments]);

    const handleSendCheckoutEmail = useCallback(async () => {
        setIsSending(true);
        try {
            await sendClientAppointmentsCheckoutEmailAsync(clientId, selectedIds);
            setModalOpen(false);
            dispatch(alertSuccess(t(TK.sendCheckoutLinkToClientEmail)));
        } catch {
            dispatch(alertError(t(TK.error)));
        } finally {
            setIsSending(false);
        }
    }, [clientId, selectedIds, dispatch, t]);

    const handleAddManualPayment = useCallback(async (paymentType: string, cashRegisterName?: string) => {
        setPaymentMenuAnchor(null);
        setIsSending(true);
        try {
            const updatedAppointments = await addPaymentToClientAppointmentsAsync(
                clientId, selectedIds, paymentType, cashRegisterName);
            dispatch({
                type: Actions.EntitiesLoaded,
                entityType: 'appointments',
                entities: updatedAppointments,
                entitiesToRemove: [],
                loadType: 'AppendResult',
            });
            setModalOpen(false);
            dispatch(alertSuccess(t(TK.addManualPayment)));
        } catch {
            dispatch(alertError(t(TK.error)));
        } finally {
            setIsSending(false);
        }
    }, [clientId, selectedIds, dispatch, t]);

    return (
        <>
            <AppButton color="secondary" onClick={open}>
                {t(TK.add)} {t(TK.payment).toLowerCase()}
            </AppButton>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {t(TK.payment)}
                </ModalHeader>
                <ModalBody style={{ padding: 'var(--spacer-S) 0' }}>
                    {unpaidAppointments.length === 0
                        ? <EmptyState>{t(TK.noUnpaidAppointments)}</EmptyState>
                        : <>
                            <SelectAllRow onClick={toggleAll}>
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={toggleAll}
                                    onClick={e => e.stopPropagation()}
                                    size="small"
                                    color="primary"
                                />
                                {t(TK.selectAppointments)}
                            </SelectAllRow>
                            {visibleAppointments.map(a => {
                                const missing = getTotalPrice(a) * (1 + a.vat) - getAppointmentTotalPayments(a);
                                const selected = selectedIds.includes(a.id);
                                return (
                                    <AppointmentRow key={a.id} $selected={selected} onClick={() => toggle(a.id)}>
                                        <Checkbox
                                            checked={selected}
                                            onChange={() => toggle(a.id)}
                                            onClick={e => e.stopPropagation()}
                                            size="small"
                                            color="primary"
                                        />
                                        <AppointmentInfo>
                                            <AppointmentDate>{toDateOnlyString(a.date)}</AppointmentDate>
                                            <AppointmentType>{serviceName(a.type, generalSettings)}</AppointmentType>
                                        </AppointmentInfo>
                                        <MissingAmount>{formatEuros(missing)}</MissingAmount>
                                    </AppointmentRow>
                                );
                            })}
                            {visibleCount < unpaidAppointments.length && (
                                <div style={{ textAlign: 'center', padding: 'var(--spacer-XS) 0' }}>
                                    <AppButton color="link" size="sm" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}>
                                        {t(TK.showMore)} ({unpaidAppointments.length - visibleCount})
                                    </AppButton>
                                </div>
                            )}
                            <TotalRow>
                                <span>{t(TK.total)}: <TotalAmount>{formatEuros(selectedTotal)}</TotalAmount></span>
                                <span>{selectedIds.length} / {unpaidAppointments.length}</span>
                            </TotalRow>
                        </>
                    }
                </ModalBody>
                <ModalFooter>
                    <FooterActions>
                        <AppButton
                            color="success"
                            disabled={selectedIds.length === 0 || isSending}
                            onClick={handleSendCheckoutEmail}
                        >
                            {t(TK.sendCheckoutLinkToClientEmail)}
                        </AppButton>
                        <AppButton
                            color="primary"
                            disabled={selectedIds.length === 0 || isSending}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => setPaymentMenuAnchor(e.currentTarget)}
                        >
                            {t(TK.addManualPayment)}
                        </AppButton>
                        <AppButton color="secondary" onClick={() => setModalOpen(false)}>
                            {t(TK.cancel)}
                        </AppButton>
                    </FooterActions>
                </ModalFooter>
            </Modal>

            <Menu
                anchorEl={paymentMenuAnchor}
                open={Boolean(paymentMenuAnchor)}
                onClose={() => setPaymentMenuAnchor(null)}
            >
                {generalSettings.paymentMethods.map(method => (
                    <MenuItem key={method} onClick={() => handleAddManualPayment(method)}>
                        <PaymentLabel paymentType={method} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ClientSendPaymentLink;
